import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Plus, Trash2, Save, Upload, X, Loader2 } from "lucide-react";
import { heroSlidesAPI, type HeroSlide } from "@/lib/db-backend-api";

const AdminHeroSlides = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [editingSlide, setEditingSlide] = useState<HeroSlide | null>(null);
  const [newSlide, setNewSlide] = useState({ image: "", title: "", subtitle: "", file: null as File | null });
  const [uploadPreview, setUploadPreview] = useState<string>("");
  const [editUploadPreview, setEditUploadPreview] = useState<string>("");
  const [editFile, setEditFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    if (auth !== 'true') {
      navigate('/admin/login');
      return;
    }
    loadSlides();
  }, [navigate]);

  const loadSlides = async () => {
    try {
      const data = await heroSlidesAPI.getAll();
      setSlides(data);
    } catch (error) {
      console.error('Error loading slides:', error);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, isEdit = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "Image size must be less than 10MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const preview = reader.result as string;
      if (isEdit) {
        setEditFile(file);
        setEditUploadPreview(preview);
      } else {
        setNewSlide({ ...newSlide, file });
        setUploadPreview(preview);
      }
    };
    reader.readAsDataURL(file);
  };

  const clearUpload = (isEdit = false) => {
    if (isEdit) {
      setEditFile(null);
      setEditUploadPreview("");
    } else {
      setNewSlide({ ...newSlide, file: null });
      setUploadPreview("");
    }
  };

  const compressImage = async (file: File, maxWidth = 1920, maxHeight = 1080, quality = 0.85): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width *= ratio;
            height *= ratio;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error('Canvas to Blob failed'));
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const uploadImage = async (file: File): Promise<string> => {
    try {
      const compressedBlob = await compressImage(file, 1920, 1080, 0.85);
      const compressedFile = new File([compressedBlob], file.name, { type: 'image/jpeg' });

      const formData = new FormData();
      formData.append('image', compressedFile);

      // @ts-ignore
      const API_URL = import.meta.env.PROD ? '/api/upload' : 'http://localhost:3003/api/upload';
      const response = await fetch(API_URL, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleAddSlide = async () => {
    if (!newSlide.file || !newSlide.title || !newSlide.subtitle) {
      toast({
        title: "Error",
        description: "Please fill all fields and upload an image",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      const imageUrl = await uploadImage(newSlide.file);
      
      await heroSlidesAPI.create({
        image: imageUrl,
        title: newSlide.title,
        subtitle: newSlide.subtitle,
        order_index: slides.length + 1,
      });

      setNewSlide({ image: "", title: "", subtitle: "", file: null });
      setUploadPreview("");
      await loadSlides();
      
      window.dispatchEvent(new CustomEvent('contentUpdated'));
      
      toast({
        title: "Success",
        description: "Slide added successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add slide",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpdateSlide = async () => {
    if (!editingSlide) return;

    setIsUploading(true);
    try {
      let imageUrl = editingSlide.image;

      if (editFile) {
        imageUrl = await uploadImage(editFile);
      }

      await heroSlidesAPI.update(editingSlide.id!, {
        image: imageUrl,
        title: editingSlide.title,
        subtitle: editingSlide.subtitle,
        order_index: editingSlide.order_index,
      });

      setEditingSlide(null);
      setEditFile(null);
      setEditUploadPreview("");
      await loadSlides();
      
      window.dispatchEvent(new CustomEvent('contentUpdated'));
      
      toast({
        title: "Success",
        description: "Slide updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update slide",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteSlide = async (id: number) => {
    if (!confirm("Are you sure you want to delete this slide?")) return;

    try {
      await heroSlidesAPI.delete(id);
      await loadSlides();
      
      window.dispatchEvent(new CustomEvent('contentUpdated'));
      
      toast({
        title: "Success",
        description: "Slide deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete slide",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Current Hero Slides ({slides.length})</CardTitle>
              <CardDescription>These are currently displayed on your homepage</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {slides.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No slides yet. Add your first slide below!</p>
                ) : (
                  slides.map((slide, index) => (
                    <div key={slide.id} className="border p-4 rounded-lg space-y-4 bg-muted/30">
                      {editingSlide?.id === slide.id ? (
                        <>
                          <div className="bg-primary/5 p-3 rounded border border-primary/20 mb-3">
                            <p className="text-xs font-semibold text-primary mb-1">EDITING SLIDE #{index + 1}</p>
                            <p className="text-xs text-muted-foreground">Changes will appear immediately on the homepage</p>
                          </div>
                          <div className="space-y-2">
                            <Label>Image</Label>
                            <div className="space-y-2">
                              {editUploadPreview || editingSlide.image ? (
                                <div className="relative">
                                  <img 
                                    src={editUploadPreview || editingSlide.image} 
                                    alt="Preview" 
                                    className="w-full h-48 object-cover rounded-lg"
                                  />
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="sm"
                                    className="absolute top-2 right-2"
                                    onClick={() => clearUpload(true)}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : null}
                              <div className="flex gap-2">
                                <Input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleImageUpload(e, true)}
                                  className="cursor-pointer"
                                />
                              </div>
                              <p className="text-xs text-muted-foreground">Upload an image (max 5MB) or use the URL field below</p>
                              <Input
                                value={editingSlide.image.startsWith('data:') ? '' : editingSlide.image}
                                onChange={(e) => setEditingSlide({ ...editingSlide, image: e.target.value })}
                                placeholder="Or paste image URL"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Title</Label>
                            <Input
                              value={editingSlide.title}
                              onChange={(e) => setEditingSlide({ ...editingSlide, title: e.target.value })}
                              placeholder="Luxury Villas in Lindos"
                            />
                            <p className="text-xs text-muted-foreground">Current: {slide.title}</p>
                          </div>
                          <div className="space-y-2">
                            <Label>Subtitle</Label>
                            <Input
                              value={editingSlide.subtitle}
                              onChange={(e) => setEditingSlide({ ...editingSlide, subtitle: e.target.value })}
                              placeholder="Experience the Magic of Rhodes Island"
                            />
                            <p className="text-xs text-muted-foreground">Current: {slide.subtitle}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={handleUpdateSlide} disabled={isUploading}>
                              {isUploading ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Save className="mr-2 h-4 w-4" />
                                  Save Changes
                                </>
                              )}
                            </Button>
                            <Button variant="outline" onClick={() => setEditingSlide(null)}>
                              Cancel
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="inline-block bg-primary/10 text-primary px-2 py-1 rounded text-xs font-semibold mb-2">
                                SLIDE #{index + 1}
                              </div>
                              <div className="space-y-2">
                                <div>
                                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Image</p>
                                  <div className="bg-background p-2 rounded border">
                                    <img 
                                      src={slide.image} 
                                      alt={slide.title} 
                                      className="w-full h-32 object-cover rounded"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Title</p>
                                  <h3 className="text-lg font-semibold">{slide.title}</h3>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Subtitle</p>
                                  <p className="text-muted-foreground">{slide.subtitle}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 pt-2 border-t">
                            <Button variant="outline" onClick={() => setEditingSlide(slide)}>
                              Edit Slide
                            </Button>
                            <Button variant="destructive" onClick={() => handleDeleteSlide(slide.id)}>
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Add New Hero Slide</CardTitle>
              <CardDescription>Create a new slide for the hero section</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="image">Image Upload</Label>
                <div className="space-y-2">
                  {uploadPreview && (
                    <div className="relative">
                      <img 
                        src={uploadPreview} 
                        alt="Preview" 
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => clearUpload()}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e)}
                      className="cursor-pointer"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Upload an image (max 5MB) or use the URL field below</p>
                  <Input
                    id="image"
                    value={newSlide.image.startsWith('data:') ? '' : newSlide.image}
                    onChange={(e) => setNewSlide({ ...newSlide, image: e.target.value })}
                    placeholder="Or paste image URL"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={newSlide.title}
                  onChange={(e) => setNewSlide({ ...newSlide, title: e.target.value })}
                  placeholder="Luxury Villas in Lindos"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subtitle">Subtitle</Label>
                <Input
                  id="subtitle"
                  value={newSlide.subtitle}
                  onChange={(e) => setNewSlide({ ...newSlide, subtitle: e.target.value })}
                  placeholder="Experience the Magic of Rhodes Island"
                />
              </div>
              <Button onClick={handleAddSlide} className="w-full" disabled={isUploading}>
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Slide
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminHeroSlides;
